import React from 'react'
import {
  Admin,
  Resource,
  CustomRoutes,
  localStorageStore,
  StoreContextProvider,
} from 'react-admin'
import { Route } from 'react-router-dom'
import AdminInvitation from './pages/AdminInvitation'

import { Dashboard } from './Dashboard'
import { Login } from './Login'

import { JobShow, JobList, JobEdit } from './resources/jobs'
import {
  ServiceConfigShow,
  ServiceConfigList,
  ServiceConfigEdit,
} from './resources/serviceConfigs'
import { UserShow, UserList, UserInvite } from './resources/users'
import { UserProfileShow, UserProfileList } from './resources/userProfiles'
import { GroupShow, GroupList, GroupCreate, GroupEdit } from './resources/groups'
import { SectionShow, SectionList, SectionCreate, SectionEdit } from './resources/sections'
import { QuestionShow, QuestionList, QuestionCreate, QuestionEdit } from './resources/questions'
import { ExamShow, ExamList, ExamCreate, ExamEdit } from './resources/exams'
import { CourseShow, CourseList, CourseCreate, CourseEdit } from './resources/courses'
import {
  ChecklistShow,
  ChecklistList,
  ChecklistCreate,
  ChecklistEdit,
} from './resources/checklists'
import { UserCourseShow, UserCourseList } from './resources/userCourses'
import { UserExamShow, UserExamList } from './resources/userExams'

// https://mui.com/material-ui/material-icons/?theme=Outlined&query=report
import JobIcon from '@mui/icons-material/Work'
import ServiceConfigIcon from '@mui/icons-material/MiscellaneousServices'
import UserIcon from '@mui/icons-material/Group'
import UserProfileIcon from '@mui/icons-material/Group'
import GroupIcon from '@mui/icons-material/SupervisedUserCircle'
import SectionIcon from '@mui/icons-material/DynamicFeed'
import QuestionIcon from '@mui/icons-material/HelpOutline'
import ExamIcon from '@mui/icons-material/Assignment'
import CourseIcon from '@mui/icons-material/School'
import ChecklistIcon from '@mui/icons-material/FactCheck'
import UserCourseIcon from '@mui/icons-material/Group'
import UserExamIcon from '@mui/icons-material/Group'

import { authProvider, buildResourcePermissions } from './authProvider'
import { dataProvider } from './dataProvider'

import MyLayout from './layout/Layout'
import { ProfileEdit } from './pages/profile'
import { customDarkTheme, customLightTheme, logInBackgroundImage } from './theme/myCustomTheme'

import { PurchaseShow, PurchaseList } from './resources/purchases'
import PurchaseIcon from '@mui/icons-material/ShoppingCart'

const resources = [
  <Resource
    key="jobs"
    name="jobs"
    list={JobList}
    edit={JobEdit}
    show={JobShow}
    recordRepresentation="scheduledAt"
    icon={JobIcon}
    options={{ label: 'Jobs' }}
  />,
  <Resource
    key="serviceConfigs"
    name="serviceConfigs"
    list={ServiceConfigList}
    edit={ServiceConfigEdit}
    show={ServiceConfigShow}
    recordRepresentation="title"
    icon={ServiceConfigIcon}
    options={{ label: 'ServiceConfigs' }}
  />,
  <Resource
    key="users"
    name="users"
    create={UserInvite}
    list={UserList}
    show={UserShow}
    recordRepresentation="email"
    icon={UserIcon}
    options={{ label: 'Users' }}
  />,
  <Resource
    key="userProfiles"
    name="userProfiles"
    list={UserProfileList}
    show={UserProfileShow}
    recordRepresentation="firstName"
    icon={UserProfileIcon}
    options={{ label: 'UserProfiles' }}
  />,
  <Resource
    key="groups"
    name="groups"
    list={GroupList}
    edit={GroupEdit}
    create={GroupCreate}
    show={GroupShow}
    recordRepresentation="title"
    icon={GroupIcon}
    options={{ label: 'Groups' }}
  />,
  <Resource
    key="sections"
    name="sections"
    list={SectionList}
    edit={SectionEdit}
    create={SectionCreate}
    show={SectionShow}
    recordRepresentation="title"
    icon={SectionIcon}
    options={{ label: 'Sections' }}
  />,
  <Resource
    key="questions"
    name="questions"
    list={QuestionList}
    edit={QuestionEdit}
    create={QuestionCreate}
    show={QuestionShow}
    recordRepresentation="question.eng"
    icon={QuestionIcon}
    options={{ label: 'Questions' }}
  />,
  <Resource
    key="exams"
    name="exams"
    list={ExamList}
    edit={ExamEdit}
    create={ExamCreate}
    show={ExamShow}
    recordRepresentation="title"
    icon={ExamIcon}
    options={{ label: 'Exams' }}
  />,
  <Resource
    key="courses"
    name="courses"
    list={CourseList}
    edit={CourseEdit}
    create={CourseCreate}
    show={CourseShow}
    recordRepresentation="title"
    icon={CourseIcon}
    options={{ label: 'Courses' }}
  />,
  <Resource
    key="checklists"
    name="checklists"
    list={ChecklistList}
    edit={ChecklistEdit}
    create={ChecklistCreate}
    show={ChecklistShow}
    recordRepresentation="title"
    icon={ChecklistIcon}
    options={{ label: 'Checklists' }}
  />,
  <Resource
    key="userCourses"
    name="userCourses"
    list={UserCourseList}
    show={UserCourseShow}
    recordRepresentation="courseId"
    icon={UserCourseIcon}
    options={{ label: 'UserCourses' }}
  />,
  <Resource
    key="userExams"
    name="userExams"
    list={UserExamList}
    show={UserExamShow}
    recordRepresentation="userId"
    icon={UserExamIcon}
    options={{ label: 'UserExams' }}
  />,
  <Resource
    name="purchases"
    list={PurchaseList}
    show={PurchaseShow}
    recordRepresentation="userId"
    icon={PurchaseIcon}
    options={{ label: 'Purchases' }}
  />,
]

const filterResources = () => {
  const resourcesMap = buildResourcePermissions()
  const resourceNames = Object.keys(resourcesMap)

  return resources
    .filter((r) => resourceNames.includes(r.props.name))
    .map((resource) => {
      const resourceName = resource.props.name
      const permissions = resourcesMap[resourceName]

      const newProps = {
        name: resource.props.name,
        recordRepresentation: resource.props.recordRepresentation,
        icon: resource.props.icon,
        options: resource.props.options,
      }

      if (permissions.isRead) {
        if (resource.props.list) {
          newProps.list =
            typeof resource.props.list === 'function'
              ? resource.props.list({
                  isEdit: permissions.isEdit || false,
                  isDelete: permissions.isDelete || false,
                  isCreate: permissions.isCreate || false,
                })
              : resource.props.list
        } else {
          newProps.list = undefined
        }
        if (resource.props.show) {
          newProps.show = resource.props.show
        } else {
          newProps.show = undefined
        }
      } else {
        newProps.list = undefined
        newProps.show = undefined
      }

      if (permissions.isEdit && resource.props.edit) {
        newProps.edit = resource.props.edit
      } else {
        newProps.edit = undefined
      }

      if (permissions.isCreate && resource.props.create) {
        newProps.create = resource.props.create
      } else {
        newProps.create = undefined
      }

      return React.cloneElement(resource, newProps)
    })
}

const store = localStorageStore(undefined, 'skipply-admin')

const App = () => (
  <Admin
    loginPage={
      <Login
        style={{
          backgroundImage: logInBackgroundImage,
        }}
      />
    }
    dataProvider={dataProvider}
    authProvider={authProvider}
    store={store}
    dashboard={Dashboard}
    layout={MyLayout}
    theme={customLightTheme}
    darkTheme={customDarkTheme}
    defaultTheme="light"
  >
    <CustomRoutes>
      <Route key="my-profile" path="/my-profile" element={<ProfileEdit />} />
    </CustomRoutes>
    {filterResources}

    <CustomRoutes noLayout disableAuthentication>
      <Route key="admin-invitation" path="/admin-invitation" element={<AdminInvitation />} />
    </CustomRoutes>
  </Admin>
)

const AppWrapper = () => (
  <StoreContextProvider value={store}>
    <App />
  </StoreContextProvider>
)

export default AppWrapper
